import { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import { useContext, useEffect } from 'react';
import { PageType } from '@invideoio/web-shared/types/Analytics';
import { AnalyticsUtils } from '@invideoio/web-shared/utils/analytics/AnalyticsUtils';
import { registerAndSendPageViewEvent } from '@invideoio/web-shared/utils/analytics/Register';

import AppLayout from '@layouts/AppLayout';

import {
  AmplitudeEvents,
  sendAmplitudeAnalyticEvent,
  sendRudderStackAnalyticEvent,
  setCommonAnalyticsProperties,
} from '@utils/analytics';
import useLazyload from '@hooks/useLazyload';
import { DispatchContext, RedirectContext, StateContext } from '@store/store';

import HeaderWithCTA from '@components/header-with-cta';
import { Referrer } from '@invideoio/web-shared/utils/utils.constant';
import { setCacheControlHeaders } from '@utils/server.utils';
import dynamic from 'next/dynamic';
import { baseAppTypeLoaded, setRedirectionLink } from 'src/store/actions';
import { MakeRootV2ResponseType } from 'src/types';
import { getMakeRootV2Page } from '@lib/API';
import Footer from '@invideoio/web-shared/components/Footer';
import { BaseAppType } from '@constants/common';
import FooterConfig from '@invideoio/web-shared/constants/footer';
import HeaderWithCTAI from '@components/header-ai';
import ErrorBoundary from '@components/ErrorBoundary';
import PromotionSection from '@components/promotion';
import {
  AnalyticsProductType,
  ClickCtaType,
  ClickCtaUxSource,
} from 'src/types/analytics.types';
import { AUTH_TAB } from '@invideoio/web-shared/components/Auth/constants';
import { AUTH_TYPE } from '@constants/homepage';
import HeroSection from '@components/make/HeroSection';
import Cards from '@components/make/Cards';
import useTranslation from 'next-translate/useTranslation';
import MakeSEO from '@components/common/SEO/MakeSEO';

const BannerSlot = dynamic(
  () => import(/* webpackChunkName: "Banner" */ '@components/banner-slot'),
);

interface MakeRootV2Props {
  makeRootPageState: MakeRootV2ResponseType;
}

const MakeRootV2 = (props: MakeRootV2Props) => {
  const { makeRootPageState } = props;
  const globalState = useContext(StateContext);

  const { platform, os, browser } = globalState;

  useLazyload();
  const router = useRouter();
  const dispatch = useContext(DispatchContext);

  const { lang } = useTranslation('make');

  const url_path =
    router.locale === 'en'
      ? router.asPath
      : `/${router.locale}${router.asPath}`;

  const {
    HeroSection: heroSection,
    BaseAppType: baseAppType,
    BottomSection: bottomSection,
    MakeCards: makeCards,
  } = makeRootPageState;
  const { goToAiSignup } = useContext(RedirectContext);
  const { handleClick } = useContext(RedirectContext);

  useEffect(() => {
    const AppName = process.env.NEXT_PUBLIC_APP_NAME;
    AnalyticsUtils.setReferrer(Referrer.WEBSITE);

    registerAndSendPageViewEvent(PageType.MAKE, {
      location: window.location.href,
    });

    sendAmplitudeAnalyticEvent(AmplitudeEvents.appLoad, {
      'Make page Revamp Treatment': 1,
    });

    if (makeRootPageState.BottomSection?.CTAUrl) {
      let redirectURL = makeRootPageState.BottomSection?.CTAUrl;
      if (!redirectURL.match(/^https?:\/\/(\S+\.)?invideo\.io/)) {
        const host = process.env.NEXT_PUBLIC_BASE_HOST || 'https://invideo.io';
        redirectURL = `${host}${redirectURL}`;
      }
      dispatch(setRedirectionLink(redirectURL));
    } else {
      const host = process.env.NEXT_PUBLIC_BASE_HOST || 'https://invideo.io';
      let redirectURL = `${host}/templates`;
      dispatch(setRedirectionLink(redirectURL));
    }

    if (makeRootPageState.BaseAppType) {
      dispatch(baseAppTypeLoaded(makeRootPageState.BaseAppType));
    }
  }, []);

  const handleCTAClick = ({
    ux_source = ClickCtaUxSource.TopRightHeader,
    type = ClickCtaType.ClickSignup,
    btn_position = 'Right Top',
    link = '',
    tab = AUTH_TAB.SIGN_UP,
    isAuthClick = false,
  }) => {
    let amplitudeSource = AmplitudeEvents.clickSignUp;

    if (tab === AUTH_TAB.LOGIN) {
      type = ClickCtaType.ClickLogin;
      amplitudeSource = AmplitudeEvents.clickLogin;
    }

    if (isAuthClick) {
      sendAmplitudeAnalyticEvent(amplitudeSource, {
        btnPosition: btn_position,
      });
      sendRudderStackAnalyticEvent({
        event_name: 'click_cta',
        event_properties: {
          ux_source: ux_source,
          type: type,
          feature_utm: 'na',
          product:
            baseAppType === BaseAppType.AI
              ? AnalyticsProductType.AI
              : AnalyticsProductType.STUDIO,
        },
      });
    }

    if (baseAppType === BaseAppType.AI) {
      if (tab === AUTH_TAB.LOGIN) {
        goToAiSignup({ page: 'login' });
      } else {
        goToAiSignup();
      }
    } else {
      handleClick(link || globalState.redirectionLink, {
        tab: AUTH_TYPE.SIGNUP,
      });
    }
  };

  return (
    <>
      {makeRootPageState.SEO ? (
        <MakeSEO
          seo={makeRootPageState.SEO}
          url={url_path}
          FAQItems={[]}
          locale={lang}
        />
      ) : (
        <></>
      )}
      <AppLayout platform={platform}>
        {baseAppType === BaseAppType.AI ? (
          <>
            <HeaderWithCTAI />
          </>
        ) : (
          <>
            <BannerSlot browser={browser} />
            <HeaderWithCTA />
          </>
        )}

        {heroSection && (
          <ErrorBoundary>
            <HeroSection heroSection={heroSection} />
          </ErrorBoundary>
        )}

        {makeCards && (
          <ErrorBoundary>
            <Cards cards={makeCards} />
          </ErrorBoundary>
        )}

        {bottomSection && (
          <ErrorBoundary>
            <PromotionSection
              promotionSection={bottomSection}
              platform={platform}
              browser={browser}
              onSignUp={handleCTAClick}
            />
          </ErrorBoundary>
        )}

        <Footer
          os={os}
          footer={FooterConfig.FOOTER}
          platform={platform}
          socialLinks={globalState.socialLinks}
        />
      </AppLayout>
    </>
  );
};

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  let makeRootPageState = null;

  try {
    makeRootPageState = await getMakeRootV2Page('/make-root-v-2', ctx.locale);
  } catch (error) {
    console.log(error);
  }

  if (!makeRootPageState) {
    return { notFound: true };
  }

  let maxage = 3600;
  if (process.env.NEXT_PUBLIC_ENV !== 'production') {
    maxage = 300; // 5mins
  }

  setCacheControlHeaders(ctx, maxage);

  return { props: { makeRootPageState } };
}

export default MakeRootV2;
